/**
 * Chatbot Module (ES6)
 *
 * Vanilla JS rewrite of the office chatbot.
 * Handles chatbot UI, conversation flow, typing effects,
 * office block rendering, and form integration via forms.js.
 */

import { showEnquiryForm, renderFormInContainer } from './forms.js';

/* ─── State ─────────────────────────────────────────────────── */

const state = {
    sessionId: null,
    isOpen: false,
    isProcessing: false,
    isFormActive: false,
    config: null,
};

/* ─── DOM references (resolved on init) ─────────────────────── */

let overlay = null;
let container = null;
let messagesEl = null;
let inputEl = null;
let formEl = null;
let typingEl = null;
let triggerTimerId = null;
let labelTimerId = null;

/* ─── Init ──────────────────────────────────────────────────── */

export function initChatbot() {
    state.config = window.CHATBOT_CONFIG || {};
    if (!state.config.chatActUrl) return;

    state.sessionId = generateSessionId();

    overlay = document.getElementById('chatbot-overlay');
    container = document.getElementById('chatbot-container');
    messagesEl = document.getElementById('chatbot-messages');
    inputEl = document.getElementById('chatbot-input');
    formEl = document.getElementById('chatbot-form');
    typingEl = document.getElementById('chatbot-typing');

    if (!overlay) return;

    bindEvents();

    // Expose for external access (admin debugging, form integration hooks)
    window.OfficeChatbot = {
        open,
        close,
        toggle,
        showEnquiryFormInChat,
        get isOpen() { return state.isOpen; },
        get isFormActive() { return state.isFormActive; },
    };
}

/* ─── Event binding ─────────────────────────────────────────── */

function bindEvents() {
    const agentOuter = document.getElementById('chat-agent-outer');

    // Toggle button
    document.querySelectorAll('.start-chat').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            toggle();
        });
    });

    // Close button
    document.getElementById('chatbot-close')?.addEventListener('click', close);

    // Form submit
    formEl?.addEventListener('submit', (e) => {
        e.preventDefault();
        sendMessage();
    });

    // Toggle send button active state when input has text
    inputEl?.addEventListener('input', () => {
        formEl?.classList.toggle('has-input', inputEl.value.trim().length > 0);
    });

    // Close on overlay click
    overlay?.addEventListener('click', (e) => {
        if (e.target === overlay) close();
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && state.isOpen) close();
    });

    // Show chat trigger: icon after 7s, label after 40s
    if (agentOuter) {
        triggerTimerId = setTimeout(() => {
            if (!agentOuter.classList.contains('chat-box-active')) {
                agentOuter.classList.add('chat-trigger-visible');
            }
        }, 7000);

        labelTimerId = setTimeout(() => {
            if (!agentOuter.classList.contains('chat-box-active')) {
                agentOuter.classList.add('chat-label-visible');
            }
        }, 40000);

        const closeBtn = agentOuter.querySelector('.chat-trigger-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                agentOuter.classList.remove('chat-label-visible');
                agentOuter.classList.add('chat-label-dismissed');
            });
        }
    }
}

/* ─── Open / Close ──────────────────────────────────────────── */

function toggle() {
    state.isOpen ? close() : open();
}

function open() {
    if (!overlay) return;
    overlay.style.display = 'flex';
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('chatbot-open');
    state.isOpen = true;
    setTimeout(() => inputEl?.focus(), 1200);

    // Clear pending trigger/label timeouts to prevent race
    clearTimeout(triggerTimerId);
    clearTimeout(labelTimerId);

    const agentOuter = document.getElementById('chat-agent-outer');
    if (agentOuter) {
        agentOuter.classList.remove('chat-label-visible');
        agentOuter.classList.add('chat-label-dismissed');
    }
}

function close() {
    if (!overlay) return;
    overlay.style.display = 'none';
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('chatbot-open');
    state.isOpen = false;
    state.isFormActive = false;

    // Reset messages padding if form was active
    if (messagesEl) {
        messagesEl.style.paddingBottom = '';
    }
}

/* ─── Send message ──────────────────────────────────────────── */

function sendMessage() {
    if (state.isProcessing) return;

    const message = inputEl?.value.trim();
    if (!message) return;

    addMessage('user', message);
    inputEl.value = '';

    const statusMsg = getStatusMessage(message);
    showTyping(statusMsg);

    state.isProcessing = true;
    callBackend(message);
}

function getStatusMessage(message) {
    if (/inquir|contact|submit|interested|send|viewing|visit|tour|arrange|request|book/i.test(message)) {
        return 'Preparing your inquiry...';
    }
    if (/cheapest|most affordable|budget|least expensive|lowest price/i.test(message)) {
        return 'Finding the cheapest options...';
    }
    if (/most expensive|premium|luxury|high-end/i.test(message)) {
        return 'Finding premium options...';
    }
    if (/gym|fitness|workout/i.test(message)) {
        return 'Finding offices with gym facilities...';
    }
    if (/parking|garage/i.test(message)) {
        return 'Finding offices with parking...';
    }
    if (/meeting room|boardroom|conference/i.test(message)) {
        return 'Finding offices with meeting rooms...';
    }

    // Try location extraction
    if (/near|in|at|around|on|off|along/i.test(message) || /office|space|find|search|looking/i.test(message)) {
        const extracted = extractLocation(message);
        if (extracted) return extracted;
        return 'Searching for offices...';
    }

    if (/detail|more|tell|info|about|show me/i.test(message)) return 'Getting details...';
    if (/price|cost|how much/i.test(message)) return 'Checking pricing...';
    if (/office|space|find|search|looking|need|want/i.test(message)) return 'Searching for offices...';

    return 'Thinking...';
}

function extractLocation(message) {
    // Postcodes
    const postcodeMatch = message.match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}|\d{5}(?:-\d{4})?|[A-Z]\d[A-Z]\s?\d[A-Z]\d)\b/i);
    if (postcodeMatch) return `Finding options near ${postcodeMatch[0]}...`;

    // Street addresses
    const streetMatch = message.match(/(?:on|near|at|along|off)\s+([A-Z][a-zA-Z\s&'-]+(?:Street|Road|Avenue|Boulevard|Drive|Lane|Way|Place|Court|Crescent|Square))/i);
    if (streetMatch?.[1]) return `Finding options on ${streetMatch[1].trim()}...`;

    // After prepositions
    const prepMatch = message.match(/(?:near|in|at|around)\s+(?:the\s+)?([A-Z][a-zA-Z\s&'-]+?)(?:\s+area|\s+district|\.|,|$|\s+for|\s+with|\s+office|\s+space)/i);
    if (prepMatch?.[1]) {
        let loc = prepMatch[1].trim().replace(/\s+(area|district|neighborhood|suburb)$/i, '');
        if (loc.length > 2 && loc.length < 40) {
            const prep = /near|around/.test(message.toLowerCase()) ? 'near' : 'in';
            return `Finding options ${prep} ${loc}...`;
        }
    }

    // Standalone location
    const standaloneMatch = message.match(/(?:office|space|find|search|looking|show|want).*?\b([A-Z][a-zA-Z\s&'-]{2,30}?)(?:\.|,|$|\s+office|\s+space|\s+for|\s+with)/i);
    if (standaloneMatch?.[1]) {
        const loc = standaloneMatch[1].trim();
        if (loc.length > 2 && loc.length < 40 && !/office|space|cheap|expensive|best|good|near|find|search|looking|show|want/i.test(loc)) {
            return `Finding options in ${loc}...`;
        }
    }

    return null;
}

/* ─── Backend call ──────────────────────────────────────────── */

function callBackend(message) {
    const payload = {
        message,
        session_id: state.sessionId,
        site: state.config.siteId,
    };

    if (window.CHATBOT_PAGE_CONTEXT) {
        payload.page_context = window.CHATBOT_PAGE_CONTEXT;
    }

    fetch(state.config.chatActUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
        .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        })
        .then((data) => {
            hideTyping();
            state.isProcessing = false;

            if (!data.success) {
                addMessage('bot', `Sorry, I encountered an error: ${data.error || 'Unknown error'}`);
                return;
            }

            if (data.session_id) {
                state.sessionId = data.session_id;
            }

            const shouldShowForm = data.show_form === true;
            const afterCallback = shouldShowForm
                ? () => setTimeout(() => showEnquiryFormInChat(data.context || {}), 800)
                : null;

            if (data.message?.trim()) {
                addMessage('bot', data.message, afterCallback);
            } else if (data.function_results && Object.keys(data.function_results).length > 0) {
                addMessage('bot', 'I found some results for you:', afterCallback);
            }
        })
        .catch((error) => {
            hideTyping();
            state.isProcessing = false;

            if (error instanceof SyntaxError) {
                addMessage('bot', 'Sorry, I received an invalid response. Please try again.');
            } else if (error.message?.startsWith('HTTP')) {
                addMessage('bot', 'Sorry, I\'m having trouble connecting. Please try again.');
            } else {
                addMessage('bot', 'Sorry, something went wrong. Please try again.');
            }
        });
}

/* ─── Messages ──────────────────────────────────────────────── */

function addMessage(type, content, callback) {
    if (!messagesEl) return;

    const messageDiv = document.createElement('div');
    messageDiv.className = `chatbot-message chatbot-message-${type}`;

    const contentDiv = document.createElement('div');
    contentDiv.className = 'chatbot-message-content';
    messageDiv.appendChild(contentDiv);

    const timeDiv = document.createElement('div');
    timeDiv.className = 'chatbot-message-time';
    timeDiv.textContent = formatTime(Date.now());
    messageDiv.appendChild(timeDiv);

    messagesEl.appendChild(messageDiv);

    if (type === 'bot') {
        typewriterEffect(contentDiv, content, callback);
    } else {
        contentDiv.innerHTML = formatMessage(content);
        if (callback) callback();
    }

    scrollToBottom();
    saveToHistory(type, content);
}

/* ─── Typewriter effect ─────────────────────────────────────── */

function typewriterEffect(element, text, callback) {
    const formattedText = formatMessage(text);

    // Split on office emoji
    const parts = formattedText.split(/(?=🏢)/);

    // Separate trailing text from last office block
    if (parts.length > 1) {
        const lastPart = parts[parts.length - 1];
        if (lastPart.startsWith('🏢')) {
            const { block, trailing } = splitOfficeBlock(lastPart);
            if (trailing.trim()) {
                parts[parts.length - 1] = block;
                parts.push(trailing);
            }
        }
    }

    let currentPart = 0;
    let currentIndex = 0;
    let currentTextSpan = null;
    const baseSpeed = 20;

    function typeNext() {
        if (currentPart >= parts.length) {
            if (callback) callback();
            return;
        }

        const part = parts[currentPart];
        const isOfficeBlock = part.startsWith('🏢');

        if (isOfficeBlock) {
            const officeDiv = document.createElement('div');
            officeDiv.className = 'chatbot-office-block';
            officeDiv.innerHTML = part;
            element.appendChild(officeDiv);
            setTimeout(() => officeDiv.classList.add('chatbot-office-block-visible'), 10);
            scrollToBottom();
            currentPart++;
            currentTextSpan = null;
            setTimeout(typeNext, 350);
        } else {
            if (currentIndex === 0) {
                currentTextSpan = document.createElement('span');
                element.appendChild(currentTextSpan);
            }

            if (currentIndex < part.length) {
                currentTextSpan.innerHTML = part.substring(0, currentIndex + 1);
                currentIndex++;
                scrollToBottom();
                setTimeout(typeNext, baseSpeed);
            } else {
                currentPart++;
                currentIndex = 0;
                currentTextSpan = null;
                setTimeout(typeNext, 10);
            }
        }
    }

    typeNext();
}

function splitOfficeBlock(text) {
    const patterns = [
        /<\/a><br>(?!\ud83c\udfe2)/,
        /(?:Inclusive of services|Ready to go|Fixed rates|Flexible contracts|24 hour access|Reception staff|Office cleaning service)[^<]*<br>(?:<br>)?(?=This |The |It |These )/i,
        /Ideal For:[\s\S]*?<br><br>(?=Would you like|How does this|Can I|Let me know|Is there|What would)/i,
        /<br><br>(?=Would you like|How does this|Can I|Let me know|Is there|What would)/i,
        /(?:amenity|feature|service|access|location|included)[^<]*<br><br>(?=Would you like|How does this|Can I)/i,
        /<br>(?:<br>)?(?=(?:This |The |It |These )(?:complex|office|building|space|location|property|center|centre|hub) (?:is |provides|offers|features|presents))/i,
        /<br><br>(?=[^<]*\?)/,
    ];

    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match) {
            const splitPoint = match.index + match[0].length;
            const block = text.substring(0, splitPoint);
            const trailing = text.substring(splitPoint).replace(/^(<br>\s*)+/, '');
            return { block, trailing };
        }
    }

    return { block: text, trailing: '' };
}

/* ─── Message formatting ────────────────────────────────────── */

function sanitiseUrl(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.href;
    } catch (e) { /* invalid URL */ }
    return null;
}

function formatMessage(text) {
    // Protect markdown links — sanitise URL scheme and escape link text
    const links = [];
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, linkText, url) => {
        const safeUrl = sanitiseUrl(url.trim());
        if (!safeUrl) return escapeHtml(linkText);
        const placeholder = `___LINK_${links.length}___`;
        links.push(`<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener">${escapeHtml(linkText)}</a>`);
        return placeholder;
    });

    // Escape remaining HTML
    text = escapeHtml(text);

    // Restore links
    links.forEach((link, index) => {
        text = text.replace(`___LINK_${index}___`, link);
    });

    // Markdown: bold, italic
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');

    // Line breaks
    text = text.replace(/\n/g, '<br>');

    // Bullet points
    text = text.replace(/^- (.+)$/gm, '<li>$1</li>');
    text = text.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

    // Post-processing: clean up AI output
    text = text.replace(/(<\/a>)\s*<br>\s*<br>/gi, '$1<br>');
    text = text.replace(/(💰\s*Price:\s*)([£$€₦₵₹R])/g, '$1from $2');
    text = text.replace(/(\d+(?:,\d{3})*(?:\.\d{2})?)\s*(per person per month)(?!\s*\(guide price\))/gi, '$1 $2 (guide price)');

    return text;
}

/* ─── Form integration ──────────────────────────────────────── */

function showEnquiryFormInChat(context) {
    if (state.isFormActive) return;
    state.isFormActive = true;

    // Map chatbot context to forms.js context format
    const formContext = {
        officeIds: context.office_ids || [],
        sourceUrl: window.location.href,
    };

    // Disable chat input while form is active
    if (inputEl) {
        inputEl.disabled = true;
        inputEl.placeholder = 'Please complete the form above to submit your inquiry...';
    }
    const sendBtn = overlay?.querySelector('.chatbot-send');
    if (sendBtn) sendBtn.disabled = true;

    // Create form wrapper and inject into messages area
    const wrapper = document.createElement('div');
    wrapper.className = 'chatbot-enquiry-form-wrapper chatbot-form-hidden';

    const formContainer = document.createElement('div');
    formContainer.className = 'chatbot-enquiry-form-container';
    wrapper.appendChild(formContainer);

    if (messagesEl) {
        messagesEl.appendChild(wrapper);

        // Render the form inside the container
        renderFormInContainer(formContainer, 'quote', formContext, {
            onClose: () => {
                state.isFormActive = false;
                if (inputEl) {
                    inputEl.disabled = false;
                    inputEl.placeholder = 'Type your message...';
                }
                if (sendBtn) sendBtn.disabled = false;
                if (messagesEl) messagesEl.style.paddingBottom = '';
                close();
            },
        });

        // Animate form in
        setTimeout(() => {
            wrapper.classList.remove('chatbot-form-hidden');
            wrapper.classList.add('chatbot-form-visible');
            messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
        }, 50);
    }
}

/* ─── Typing indicator ──────────────────────────────────────── */

function showTyping(statusText) {
    if (!typingEl) return;

    let statusSpan = typingEl.querySelector('.chatbot-typing-status');
    if (!statusSpan) {
        statusSpan = document.createElement('span');
        statusSpan.className = 'chatbot-typing-status';
        typingEl.appendChild(statusSpan);
    }

    statusSpan.textContent = statusText || 'Thinking...';
    typingEl.style.display = 'flex';
    scrollToBottom();
}

function hideTyping() {
    if (typingEl) typingEl.style.display = 'none';
}

/* ─── Scroll ────────────────────────────────────────────────── */

function scrollToBottom() {
    if (state.isFormActive) return;
    if (messagesEl) {
        setTimeout(() => {
            messagesEl.scrollTop = messagesEl.scrollHeight;
        }, 100);
    }
}

/* ─── History ───────────────────────────────────────────────── */

function saveToHistory(type, content) {
    const key = `${state.config.sessionKey || 'chatbot'}_history`;
    try {
        let history = JSON.parse(localStorage.getItem(key) || '[]');
        history.push({ type, content, timestamp: Date.now() });
        if (history.length > 50) history = history.slice(-50);
        localStorage.setItem(key, JSON.stringify(history));
    } catch {
        // localStorage not available
    }
}

/* ─── Utilities ─────────────────────────────────────────────── */

function generateSessionId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function formatTime(timestamp) {
    const date = new Date(timestamp);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
}

function escapeHtml(text) {
    return String(text ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}
